// NATIVE
import React from 'react';

// INSTALLED
import { Card, CardContent, Typography, Box, Chip } from '@mui/material';

// CUSTOM

/**
 * MiniCardGlobal component displays a card with a title, count, icon, and additional text.
 * It is styled using Material-UI components and accepts various props for customization.
 *
 * @param {object} props - The properties object.
 * @param {string} props.title - The title text displayed on the card.
 * @param {number} props.count - The count number displayed prominently on the card.
 * @param {React.ReactNode} props.icon - The icon displayed on the card.
 * @param {string} props.colorBgIcon - The background color for the icon container.
 * @param {string} props.totalText - Additional text displayed below the count.
 * @param {string} props.colorTitle - The color of the title text.
 * @param {string} props.bgTitle - The background color of the title chip.
 * @returns {JSX.Element} The rendered MiniCardGlobal component.
 */
export const MiniCardGlobal = ({
  title,
  count,
  icon,
  colorBgIcon,
  totalText,
  colorTitle,
  bgTitle,
}: any) => {
  return (
    <Card
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        p: 2,
        borderRadius: 2,
        boxShadow: 3,
        bgcolor: 'background.paper',
        maxWidth: 270,
      }}
    >
      <CardContent
        sx={{
          p: 0,
          '&:last-child': {
            paddingBottom: 0, // Remove padding-bottom for the last child
          },
        }}
      >
        <Typography variant="subtitle2" color="text.secondary">
          <Chip
            size="small"
            label={title}
            sx={{ color: colorTitle, background: bgTitle, fontWeight: 700 }}
          />
        </Typography>
        <Typography
          variant="h6"
          color="text.primary"
          sx={{ fontSize: '1.5rem', fontWeight: 400, lineHeight: 1.5, letterSpacing: 0.7 }}
        >
          {count}
        </Typography>
        <Typography
          variant="h6"
          color="text.primary"
          sx={{ fontSize: '0.8rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: 0.7 }}
        >
          {totalText}
        </Typography>
      </CardContent>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 1.5,
          borderRadius: '7%',
          bgcolor: colorBgIcon,
          color: 'primary.contrastText',
        }}
      >
        {icon}
      </Box>
    </Card>
  );
};
